import {
  CRYPTOBOT_ICON_BASE64,
  XROCKET_ICON_BASE64,
} from "./providerIconsBase64"

// ─── Sheet height ─────────────────────────────────────────────────────────────

/** Sheet height in px when open — from Telegram stable viewport, not lv/dvh. */
export function calcSheetHeightPx(open: boolean): number | null {
  if (!open || typeof window === "undefined") return null
  const tg = window.Telegram?.WebApp
  const stable =
    typeof tg?.viewportStableHeight === "number" && tg.viewportStableHeight > 1
      ? tg.viewportStableHeight
      : window.innerHeight
  const safeTop =
    typeof tg?.safeAreaInset?.top === "number" && tg.safeAreaInset.top > 0
      ? tg.safeAreaInset.top
      : 0
  return Math.max(240, Math.round(stable * 0.95) - safeTop)
}

/** True if tapping this target should NOT blur the amount input. */
export function pointerTargetKeepsAmountKeyboard(
  target: EventTarget | null
): boolean {
  const el = target instanceof Element ? target : null
  if (!el) return false
  if (el.closest("[data-amount-field]")) return true
  return (
    el.closest('button, a, input, textarea, select, [role="button"]') != null
  )
}

// ─── Icons ────────────────────────────────────────────────────────────────────

export const TonProviderIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.97502 0C15.4841 0 19.95 4.466 19.95 9.97508C19.95 15.4841 15.4841 19.95 9.97502 19.95C4.46596 19.95 1.5528e-05 15.4841 0 9.97508C0 4.46601 4.46595 1.34663e-05 9.97502 0Z"
      fill="#4DB8FF"
    />
    <path
      d="M6.25326 5.24862C4.8675 5.24862 3.98886 6.7436 4.68614 7.95227L9.27966 15.9141C9.5881 16.4491 10.3613 16.4491 10.6697 15.9141L15.2641 7.95227C15.9605 6.74549 15.0819 5.24863 13.697 5.24862H6.25326ZM13.6952 6.65324C14.0111 6.65324 14.197 6.98834 14.0464 7.24955L11.6334 11.5688L11.633 11.5695L10.6756 13.4216V6.65324H13.6952ZM9.2719 6.65413V13.4197L8.31541 11.5686L8.31505 11.5679L5.90122 7.25073L5.89425 7.23816C5.75641 6.97885 5.94135 6.65413 6.25231 6.65413H9.2719Z"
      fill="white"
    />
  </svg>
)

export function Base64ProviderIcon({
  base64,
  fallbackColor,
  fallbackText,
}: {
  base64: string
  fallbackColor: string
  fallbackText: string
}) {
  if (!base64) {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="10" cy="10" r="10" fill={fallbackColor} />
        <text
          x="10"
          y="14"
          textAnchor="middle"
          fill="white"
          fontSize="9"
          fontFamily="sans-serif"
          fontWeight="bold"
        >
          {fallbackText}
        </text>
      </svg>
    )
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={`clip-${fallbackText}`}>
          <circle cx="10" cy="10" r="10" />
        </clipPath>
      </defs>
      <image
        href={`data:image/png;base64,${base64}`}
        x="0"
        y="0"
        width="20"
        height="20"
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#clip-${fallbackText})`}
      />
    </svg>
  )
}

export const CryptoBotProviderIcon = () => (
  <Base64ProviderIcon
    base64={CRYPTOBOT_ICON_BASE64}
    fallbackColor="#26A5E4"
    fallbackText="CB"
  />
)

export const XRocketProviderIcon = () => (
  <Base64ProviderIcon
    base64={XROCKET_ICON_BASE64}
    fallbackColor="#FF5C28"
    fallbackText="XR"
  />
)

export function ProviderIcon({ name }: { name: string }) {
  if (name === "TON") return <TonProviderIcon />
  if (name === "CryptoBot") return <CryptoBotProviderIcon />
  return <XRocketProviderIcon />
}

export const WalletIcon = () => (
  <svg
    width="16"
    height="15"
    viewBox="0 0 13 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 10V10.6667C12 11.0203 11.8595 11.3594 11.6095 11.6095C11.3594 11.8595 11.0203 12 10.6667 12H1.33333C0.979711 12 0.640573 11.8595 0.390524 11.6095C0.140476 11.3594 0 11.0203 0 10.6667V1.33333C0 0.979711 0.140476 0.640573 0.390524 0.390524C0.640573 0.140476 0.979711 0 1.33333 0H10.6667C11.0203 0 11.3594 0.140476 11.6095 0.390524C11.8595 0.640573 12 0.979711 12 1.33333V2H6C5.64638 2 5.30724 2.14048 5.05719 2.39052C4.80714 2.64057 4.66667 2.97971 4.66667 3.33333V8.66667C4.66667 9.02029 4.80714 9.35943 5.05719 9.60948C5.30724 9.85952 5.64638 10 6 10M6 8.66667H12.6667V3.33333H6M8.66667 7C8.40145 7 8.1471 6.89464 7.95956 6.70711C7.77202 6.51957 7.66667 6.26522 7.66667 6C7.66667 5.73478 7.77202 5.48043 7.95956 5.29289C8.1471 5.10536 8.40145 5 8.66667 5C8.93188 5 9.18624 5.10536 9.37377 5.29289C9.56131 5.48043 9.66667 5.73478 9.66667 6C9.66667 6.26522 9.56131 6.51957 9.37377 6.70711C9.18624 6.89464 8.93188 7 8.66667 7Z"
      fill="white"
      fillOpacity="0.6"
    />
  </svg>
)
